import cx from 'classnames';
import { t } from 'i18next';
import _ from 'lodash';
import { Component, ReactElement, useRef } from 'react';
import { connect } from 'react-redux';
import { RioLoader } from '../../../../../../shared/frontend/components/loader';
import { withSiteMeta } from '../../../../../../shared/frontend/HOCS/withSiteMeta';
import { TGuildSummary } from '../../../../shared/types/guildTypes';
import RaidLoggedEncounterActionCreator from '../../actions/raidLoggedEncounterActionCreator';
import { RaidLoggedEncounterRow } from './raidLoggedEncounterRow';
import type { TBossKillHeaderVariant } from '../container/encounterDetailsTooltipContent';

export type TRaidLoggedEncounterTableProps = {
  encounters: any[];
  columns: string[];
  bossKillHeaderVariant?: TBossKillHeaderVariant;
  guild: TGuildSummary;
} & any;

/**
 * RaidLoggedEncounterTableBase
 */
const RaidLoggedEncounterTableBase = withSiteMeta(
  class extends Component<TRaidLoggedEncounterTableProps, {}> {
    public render(): ReactElement<TRaidLoggedEncounterTableProps> {
      if (this.props.component) {
        return <this.props.component {...this.props} />;
      } else {
        return <RaidLoggedEncounterTableLarge {...this.props} />;
      }
    }
  }
);

function RaidLoggedEncounterTableLarge(props: TRaidLoggedEncounterTableProps) {
  const columns = new Set(props.columns);
  const theadRowRef = useRef<HTMLTableRowElement>(null);

  const totalColumns = theadRowRef.current?.children.length;

  return (
    <table
      className={cx(
        'slds-table',
        'slds-table--bordered',
        'slds-table--striped',
        'rio-guild-details-table',
        'slds-no-row-hover'
      )}
      style={{ overflow: 'hidden' }}
    >
      <thead>
        <tr ref={theadRowRef}>
          <th style={{ width: 1 }}></th>
          {columns.has('id') && (
            <th style={{ width: 1 }} className="slds-text-align--center text-white slds-text-heading--small">
              {t('encounter-details:table.columns.id')}
            </th>
          )}
          {columns.has('guild') && (
            <th className="slds-text-align--left text-white slds-text-heading--small">
              {t('encounter-details:table.columns.guild')}
            </th>
          )}

          {columns.has('boss') && (
            <th
              style={{ width: '8rem', maxWidth: '8rem' }}
              className="slds-text-align--left text-white slds-text-heading--small"
            >
              {t('encounter-details:table.columns.boss')}
            </th>
          )}
          {columns.has('pull_number') && (
            <th style={{ width: '3.5rem' }} className="slds-text-align--center text-white slds-text-heading--small">
              {t('encounter-details:table.columns.pull')}
            </th>
          )}
          {columns.has('overall_percent') && (
            <th className="slds-text-align--right slds-text-heading--small">
              {t('encounter-details:table.columns.boss-percent')}
            </th>
          )}

          {columns.has('duration') && (
            <th className="slds-text-align--center text-white slds-text-heading--small">
              {t('encounter-details:table.columns.duration')}
            </th>
          )}
          {columns.has('item_level') && (
            <th className="slds-text-align--center slds-text-heading--small">
              {t('encounter-details:table.columns.item-level')}
            </th>
          )}
          {columns.has('deaths') && (
            <th className="slds-text-align--center slds-text-heading--small">
              {t('encounter-details:table.columns.deaths')}
            </th>
          )}
          {columns.has('composition') && (
            <th className="slds-text-align--left text-white slds-text-heading--small">
              {t('encounter-details:table.columns.raid-composition')}
            </th>
          )}
          {columns.has('relative_date') && (
            <th style={{ width: 1 }} className="slds-text-align--center text-white slds-text-heading--small">
              {t('encounter-details:table.columns.when')}
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {props.isLoading ? (
          <tr>
            <td colSpan={totalColumns}>
              <div className="empty-content-wrapper">
                <RioLoader size="xl" />
              </div>
            </td>
          </tr>
        ) : (
          <>
            {props.encounters.length === 0 && (
              <tr>
                <td colSpan={totalColumns}>
                  <div className="slds-align--absolute-center" style={{ minHeight: '250px', whiteSpace: 'normal' }}>
                    <div className="slds-text-align--center">
                      <p className="slds-text-heading--medium">{t('common:errors.empty-character-leaderboard')}</p>
                    </div>
                  </div>
                </td>
              </tr>
            )}

            {props.encounters.map((encounter: any, index: number) => (
              <RaidLoggedEncounterRow
                key={encounter.id}
                isOpen={props.openLoggedEncounters[encounter.id]}
                toggleOpen={props.toggleOpenRun}
                usesPrivacy={props.usesPrivacy}
                columns={columns}
                encounter={encounter}
                previousEncounter={
                  index < props.encounters.length - 1
                    ? _.find(props.encounters.slice(index + 1), { is_reset: false })
                    : null
                }
                guild={props.guild}
                bossKillHeaderVariant={props.bossKillHeaderVariant}
              />
            ))}
          </>
        )}
      </tbody>
    </table>
  );
}

function mapStateToProps(state: any, ownProps: any): any {
  return {
    ...ownProps,
    openLoggedEncounters: state.raidLoggedEncounter.openLoggedEncounters
  };
}

function mapRaidLoggedEncounterTableDispatchToProps(dispatch: Function, ownProps: any): any {
  return {
    toggleOpenRun(id: number) {
      dispatch(RaidLoggedEncounterActionCreator.toggleOpenLoggedEncounter(id));
    }
  };
}

export const RaidLoggedEncounterTable = connect<any, any, any>(
  mapStateToProps,
  mapRaidLoggedEncounterTableDispatchToProps
)(RaidLoggedEncounterTableBase as any);
